import {
  BodyText,
  Button,
  Callout,
  Checkbox,
  Collapsible,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Textarea,
  toast,
} from "@/components/ui";
import { Field } from "@/components/ui";
import { formatPhoneNumber } from "@/lib/formatters";
import type { Location } from "@curai/js-fhir-tools";
import { MedicationRequest } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { getCoding } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import { LoaderCircle, OctagonX, Trash2 } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import type { z } from "zod";
import { PharmacyModal } from "../../../../components/PharmacySearch/PharmacySearchModal";
import { useCancelPrescription } from "../../network/useCancelPrescription";
import { useDispenseUnits } from "../../network/useDispenseUnits";
import { useManualRoutePrescription } from "../../network/useManualRoutePrescription";
import { usePharmacyRoutePrescription } from "../../network/usePharmacyRoutePrescription";
import {
  RouteToPharmacyUnavailableReason,
  type RxItem,
} from "../../network/usePrescriptions";
import { useUpdatePrescription } from "../../network/useUpdatePrescription";
import { useBatchApplyPharmacy } from "../network/useBatchApplyPharmacy";
import { useInteractionChecking } from "../network/useInteractionChecking";
import { AlternativePharmacies } from "./AlternativePharmacies/AlternativePharmacies";
import { FormularyButton } from "./Formulary/FormularyButton";
import { Interactions } from "./Interactions";
import { PrescriptionBenefitsButton } from "./PrescriptionBenefits/PrescriptionBenefitsButton";
import type { AlternativeDispensingPharmacyLocation } from "./network/useAlternativeDispensingPharmacies";
import { medicationSchema } from "./schema";

interface MedicationProps {
  rxItem: RxItem;
  defaultOpen?: boolean;
  readOnly?: boolean;
  registeredWithErxVendor?: boolean;
  isAmazon?: boolean;
}

export const Medication = ({
  rxItem,
  defaultOpen,
  readOnly,
  registeredWithErxVendor,
  isAmazon = false,
}: MedicationProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const [openPharmacy, setOpenPharmacy] = useState(false);
  const [openCollapsible, setOpenCollapsible] = useState(true);

  const medicationRequest = rxItem.medicationRequest;
  const { data: dispenseUnitsData } = useDispenseUnits();
  const { data: isAmazonPrimeDeliveryEnabled } = useConfigCatFeatureFlag(
    "amazon-prime-rx-delivery",
  );

  const {
    data: interactionCheckingData,
    isError: isInteractionCheckingError,
    isFetching: isInteractionCheckingPending,
    refetch: refetchInteractionChecking,
  } = useInteractionChecking({
    patientId,
    medicationRequestId: medicationRequest.id as string,
    dispensableDrugId: medicationRequest.dispensableDrugId,
    enabled: registeredWithErxVendor,
  });

  const hasInteraction =
    (interactionCheckingData?.drugDrugInteractions?.length ?? 0) > 0 ||
    (interactionCheckingData?.drugAllergyInteractions?.length ?? 0) > 0;

  const updatePrescription = useUpdatePrescription({
    patientId,
    encounterId: medicationRequest.encounterId,
    medicationRequestId: medicationRequest.id as string,
  });

  const cancelPrescription = useCancelPrescription({
    patientId,
    encounterId: medicationRequest.encounterId,
    medicationRequestId: medicationRequest.id as string,
  });
  const manualRoutePrescription = useManualRoutePrescription({
    patientId,
    encounterId: medicationRequest.encounterId,
    medicationRequestId: medicationRequest.id as string,
  });
  const pharmacyRoutePrescription = usePharmacyRoutePrescription({
    patientId,
    encounterId: medicationRequest.encounterId,
    medicationRequestId: medicationRequest.id as string,
  });
  const batchApplyPharmacy = useBatchApplyPharmacy({
    patientId,
    encounterId: medicationRequest.encounterId,
  });

  const isAnyActionLoading =
    updatePrescription.isPending ||
    cancelPrescription.isPending ||
    pharmacyRoutePrescription.isPending ||
    manualRoutePrescription.isPending ||
    batchApplyPharmacy.isPending;

  const disabled =
    !rxItem.canEdit ||
    readOnly ||
    pharmacyRoutePrescription.isPending ||
    manualRoutePrescription.isPending ||
    batchApplyPharmacy.isPending;

  const form = useForm<z.infer<typeof medicationSchema>>({
    resolver: zodResolver(medicationSchema),
    mode: "onChange",
    defaultValues: {
      sig: medicationRequest.sig || "",
      dispenseQuantity: medicationRequest.dispenseQuantity || undefined,
      dispenseUnit: medicationRequest.dispenseUnit?.coding?.[0]?.code || "",
      daysSupply: medicationRequest.daysSupply || undefined,
      refills: medicationRequest.refills || undefined,
      notes: medicationRequest.noteToPharmacy || "",
      substitutionsAllowed: Boolean(medicationRequest.substitutionAllowed),
      pharmacyId: medicationRequest.pharmacy?.identifier?.[0]?.value || "",
      pharmacySystem: medicationRequest.pharmacy?.identifier?.[0]?.system || "",
      overrideReason: medicationRequest.overrideReason || "",
    },
  });

  const ndc = medicationRequest.medicationCodeableConcept
    ? (getCoding(
        medicationRequest.medicationCodeableConcept,
        MedicationRequest.DRUG_IDENTIFIER_SYSTEMS.NDC_SYSTEM_URI,
      )?.code?.slice(0, 24) ?? null)
    : null;

  const dispenseUnitCode = form.watch("dispenseUnit");
  // Dosespot dispense unit codes are integers — Number() conversion is intentional
  const dispenseUnitTypeId = dispenseUnitCode ? Number(dispenseUnitCode) : null;
  const pharmacyIdValue = form.watch("pharmacyId");
  const daysSupplyValue = form.watch("daysSupply");
  const dispenseQuantityValue = form.watch("dispenseQuantity");
  const canViewBenefits = Boolean(
    ndc &&
      pharmacyIdValue &&
      daysSupplyValue &&
      dispenseQuantityValue &&
      dispenseUnitTypeId,
  );

  const savePrescription = async (values: z.infer<typeof medicationSchema>) => {
    const unit = dispenseUnitsData?.dispenseUnits.find(
      (unit) => unit.coding?.[0].code === values.dispenseUnit,
    );
    try {
      await updatePrescription.mutateAsync({
        prescription: {
          dispense: {
            daysSupply: values.daysSupply,
            quantity: values.dispenseQuantity,
            unit,
            refills: values.refills,
          },
          sig: values.sig,
          noteToPharmacy: values.notes,
          substitutionAllowed: values.substitutionsAllowed,
          pharmacy: {
            vendorId: values.pharmacyId,
            vendorSystem: values.pharmacySystem,
          },
          overrideReason: values.overrideReason,
        },
      });
    } catch {
      toast.error("Failed to update prescription.");
    }
  };

  const onSubmit = async (values: z.infer<typeof medicationSchema>) => {
    trackEvent(TRACKERS.SAVE_NEW_MEDICATION);
    await savePrescription(values);
  };

  const handleSelectAlternativePharmacy = async (
    loc: AlternativeDispensingPharmacyLocation,
  ) => {
    trackEvent(TRACKERS.SELECT_ALTERNATIVE_PHARMACY);
    await savePrescription({
      ...form.getValues(),
      pharmacyId: loc.pharmacyId.toString(),
      pharmacySystem: loc.pharmacyIdSystem,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Resource update to trigger form reset
  useEffect(() => {
    form.reset({
      sig: medicationRequest.sig || "",
      dispenseQuantity: medicationRequest.dispenseQuantity,
      dispenseUnit: medicationRequest.dispenseUnit?.coding?.[0]?.code || "",
      daysSupply: medicationRequest.daysSupply,
      refills: medicationRequest.refills,
      notes: medicationRequest.noteToPharmacy || "",
      substitutionsAllowed: Boolean(medicationRequest.substitutionAllowed),
      pharmacyId: medicationRequest.pharmacy?.identifier?.[0]?.value || "",
      pharmacySystem: medicationRequest.pharmacy?.identifier?.[0]?.system || "",
      overrideReason: medicationRequest.overrideReason || "",
    });
  }, [medicationRequest?.meta?.versionId, form]);

  const onSubmitRoutePharmacy = async () => {
    trackEvent(TRACKERS.SEND_MEDICATION_TO_PHARMACY);
    try {
      if (!medicationRequest.id)
        throw new Error("Medication request ID not found");

      if (!medicationRequest.pharmacy) throw new Error("Pharmacy not found");

      const res = await pharmacyRoutePrescription.mutateAsync({
        medicationRequestIds: [medicationRequest.id],
      });
      if (res.data.failures.length > 0) {
        const failure = res.data.failures[0];
        toast.error(
          `Failed to submit prescription to pharmacy: ${failure.failureReason}`,
        );
      }
    } catch {
      toast.error("Failed to submit prescription to pharmacy.");
    }
  };

  const onPhoneIn = async () => {
    trackEvent(TRACKERS.MARK_PHONED_IN);
    try {
      if (!medicationRequest.pharmacy) throw new Error("Pharmacy not found");

      await manualRoutePrescription.mutateAsync({
        routingMethod: MedicationRequest.ROUTING_METHOD.values.PHONE,
      });
    } catch {
      toast.error("Failed to manually route prescription.");
    }
  };

  const handleClickCancel = async () => {
    try {
      await cancelPrescription.mutateAsync();
    } catch {
      toast.error("Failed to cancel prescription.");
    }
  };

  const renderPharmacy = () => {
    if (!medicationRequest?.pharmacy) {
      return null;
    }

    const location = medicationRequest?.pharmacy;
    const { city, line, postalCode, state } = location.address || {};
    const [phone] = location.telecom || [];
    return (
      <div className="flex flex-col">
        {rxItem.routeToPharmacyUnavailableReasons.includes(
          RouteToPharmacyUnavailableReason.PHARMACY_MISSING_VENDOR_ID,
        ) && (
          <Callout
            title="Pharmacy Not Found"
            description="This pharmacy was not found in Dosespot. Click Change Pharmacy to search for a new pharmacy."
            variant="danger"
          />
        )}
        <BodyText size="sm">{location.name}</BodyText>
        <BodyText size="sm">{line}</BodyText>
        <BodyText size="sm">
          {city}, {state} {postalCode}
        </BodyText>
        <BodyText size="sm">{formatPhoneNumber(phone?.value)}</BodyText>
      </div>
    );
  };

  const titleIcon = useMemo(() => {
    if (hasInteraction || isInteractionCheckingError) {
      return <OctagonX size={16} className="stroke-error" />;
    }
    if (isInteractionCheckingPending) {
      return <LoaderCircle size={16} className="animate-spin" />;
    }
    return undefined;
  }, [
    hasInteraction,
    isInteractionCheckingError,
    isInteractionCheckingPending,
  ]);

  const menuActions = [
    {
      label: "Cancel Prescription",
      icon: <Trash2 size={16} />,
      onClick: handleClickCancel,
      variant: "danger" as const,
      disabled: !rxItem.canCancel || readOnly || isAnyActionLoading,
      isLoading: cancelPrescription.isPending,
    },
  ];

  const uniqId = useId();

  return (
    <Collapsible
      title={medicationRequest.medicationName}
      titleIcon={titleIcon}
      headerSize="sm"
      background="secondary"
      status={
        medicationRequest.routingMethod ===
        MedicationRequest.ROUTING_METHOD.values.PHONE
          ? "Phoned In"
          : medicationRequest.dosespotStatusLabel
      }
      defaultOpen={defaultOpen}
      open={openCollapsible}
      controlled={true}
      onOpenChange={setOpenCollapsible}
      forcedDropdownActions={menuActions}
      customActions={
        ndc
          ? [
              <FormularyButton
                key="formulary"
                ndc={ndc}
                medicationName={medicationRequest.medicationName}
              />,
              <PrescriptionBenefitsButton
                key="benefits"
                ndc={ndc}
                medicationName={medicationRequest.medicationName}
                pharmacyId={pharmacyIdValue ? Number(pharmacyIdValue) : null}
                quantity={dispenseQuantityValue ?? null}
                daysSupply={daysSupplyValue ?? null}
                dispenseUnitTypeId={dispenseUnitTypeId}
                pharmacy={medicationRequest.pharmacy}
                disabled={!canViewBenefits}
              />,
            ]
          : []
      }
    >
      {hasInteraction &&
        !isInteractionCheckingError &&
        interactionCheckingData && (
          <Interactions
            interactionCheckingData={interactionCheckingData}
            medicationRequestId={medicationRequest.id as string}
          />
        )}

      {isInteractionCheckingError && (
        <Callout
          title="Error"
          variant="danger"
          description="Failed to check for interactions. Click here to try again."
          className="cursor-pointer [&_*]:cursor-pointer"
          onClick={() => refetchInteractionChecking()}
        />
      )}
      {rxItem.canRetrySendToPharmacy && (
        <Callout
          title="Routing Failed"
          description="This prescription failed to route to the pharmacy. Click the Resend to Pharmacy button to try again, or call the pharmacy and mark the prescription as phoned in."
          variant="danger"
        />
      )}
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-3"
      >
        <div className="grid grid-cols-2 gap-2">
          {hasInteraction && (
            <Controller
              disabled={disabled}
              control={form.control}
              name="overrideReason"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid} className="col-span-2">
                  <FieldLabel>Interaction Override Reason*</FieldLabel>
                  <Input {...field} />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />
          )}

          <Controller
            disabled={disabled}
            control={form.control}
            name="sig"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid} className="col-span-2">
                <FieldLabel htmlFor={`${uniqId}-${field.name}`}>
                  Patient Instructions*
                </FieldLabel>
                <Textarea
                  id={`${uniqId}-${field.name}`}
                  {...field}
                  maxLength={1000}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          <Controller
            disabled={disabled}
            control={form.control}
            name="dispenseQuantity"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={`${uniqId}-${field.name}`}>
                  Dispense Quantity*
                </FieldLabel>
                <Input id={`${uniqId}-${field.name}`} {...field} />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="dispenseUnit"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid} className="w-full">
                <FieldLabel htmlFor={`${uniqId}-${field.name}`}>
                  Dispense Unit*
                </FieldLabel>

                <Select
                  disabled={disabled}
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <SelectTrigger id={`${uniqId}-${field.name}`}>
                    <SelectValue placeholder="Select Dispense" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {dispenseUnitsData?.dispenseUnits.map((unit) => (
                        <SelectItem
                          key={unit.text}
                          value={unit.coding?.[0].code || "unknown"}
                        >
                          {unit.text}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          <Controller
            disabled={disabled}
            control={form.control}
            name="daysSupply"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={`${uniqId}-${field.name}`}>
                  Days Supply*
                </FieldLabel>
                <Input id={`${uniqId}-${field.name}`} {...field} />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          <Controller
            disabled={disabled}
            control={form.control}
            name="refills"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={`${uniqId}-${field.name}`}>
                  Refills*
                </FieldLabel>
                <Input id={`${uniqId}-${field.name}`} {...field} />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          <Controller
            disabled={disabled}
            control={form.control}
            name="notes"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid} className=" col-span-2">
                <FieldLabel htmlFor={`${uniqId}-${field.name}`}>
                  Notes for Pharmacy
                </FieldLabel>
                <Textarea
                  id={`${uniqId}-${field.name}`}
                  {...field}
                  // NCPDP 2017071 is 210, but Rxs on Amazon patients need extra space reserved for prefixes appended during eRx transmission
                  maxLength={isAmazon ? 190 : 210}
                  className="h-12"
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="substitutionsAllowed"
            render={({ field, fieldState }) => (
              <Field
                data-invalid={fieldState.invalid}
                className="col-span-2 flex items-center gap-2 space-y-0"
              >
                <FieldLabel
                  htmlFor={`${uniqId}-${field.name}`}
                  className="w-fit"
                >
                  Substitutions Allowed
                </FieldLabel>
                <Checkbox
                  disabled={disabled}
                  checked={field.value}
                  onCheckedChange={(v) => field.onChange(v)}
                  id={`${uniqId}-${field.name}`}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
        </div>

        <Separator />
        {renderPharmacy()}
        {rxItem.canEdit && !readOnly && (
          <Button
            size="sm"
            className="w-fit underline"
            variant="link"
            onClick={() => {
              setOpenPharmacy(true);
              trackEvent(TRACKERS.CHANGE_PHARMACY);
            }}
          >
            {medicationRequest.pharmacy ? "Change Pharmacy" : "Add Pharmacy"}
          </Button>
        )}

        {isAmazonPrimeDeliveryEnabled && (
          <AlternativePharmacies
            patientId={patientId}
            encounterId={medicationRequest.encounterId}
            medicationRequestId={medicationRequest.id as string}
            currentPharmacyVendorId={
              medicationRequest.pharmacy?.identifier?.[0]?.value
            }
            onSelectPharmacy={handleSelectAlternativePharmacy}
            isSaving={updatePrescription.isPending}
            readOnly={readOnly || !rxItem.canEdit}
          />
        )}

        <div className="flex justify-end items-center w-full">
          {form.formState.isValid && (
            <div className="flex gap-2 w-fit">
              {rxItem.canEdit && (
                <Button
                  size="xs"
                  type="submit"
                  isLoading={updatePrescription.isPending}
                  disabled={
                    !form.formState.isDirty || readOnly || isAnyActionLoading
                  }
                >
                  Save
                </Button>
              )}

              {rxItem.canMarkPhonedIn && (
                <Button
                  size="xs"
                  variant="secondary"
                  onClick={onPhoneIn}
                  isLoading={manualRoutePrescription.isPending}
                  disabled={
                    !rxItem.canMarkPhonedIn ||
                    readOnly ||
                    isAnyActionLoading ||
                    form.formState.isDirty
                  }
                >
                  Mark Phoned In
                </Button>
              )}
              {rxItem.canSendToPharmacy && (
                <Button
                  onClick={onSubmitRoutePharmacy}
                  size="xs"
                  variant="primary"
                  isLoading={pharmacyRoutePrescription.isPending}
                  disabled={
                    readOnly ||
                    form.formState.isDirty ||
                    !rxItem.canSendToPharmacy ||
                    isAnyActionLoading
                  }
                >
                  Send to Pharmacy
                </Button>
              )}
              {rxItem.canRetrySendToPharmacy && (
                <Button
                  size="xs"
                  variant="primary"
                  onClick={onSubmitRoutePharmacy}
                  isLoading={pharmacyRoutePrescription.isPending}
                  disabled={
                    !rxItem.canRetrySendToPharmacy ||
                    readOnly ||
                    isAnyActionLoading
                  }
                >
                  Resend to Pharmacy
                </Button>
              )}
            </div>
          )}
        </div>
      </form>
      <PharmacyModal
        patientId={patientId}
        open={openPharmacy && !readOnly}
        onOpenChange={setOpenPharmacy}
        medicationRequest={medicationRequest}
        applyToCurrentMedication={(pharmacy: Location) => {
          if (pharmacy) {
            form.setValue("pharmacyId", pharmacy.identifier?.[0]?.value || "");
            onSubmit({
              ...form.getValues(),
              pharmacyId: pharmacy.identifier?.[0]?.value || "",
              pharmacySystem: pharmacy.identifier?.[0]?.system || "",
            });
            setOpenPharmacy(false);
          }
        }}
        applyToAllMedications={async (pharmacy: Location) => {
          if (pharmacy) {
            form.setValue("pharmacyId", pharmacy.identifier?.[0]?.value || "");
            await onSubmit({
              ...form.getValues(),
              pharmacyId: pharmacy.identifier?.[0]?.value || "",
              pharmacySystem: pharmacy.identifier?.[0]?.system || "",
            });
            await batchApplyPharmacy.mutateAsync({
              pharmacyId: pharmacy.identifier?.[0]?.value || "",
            });
            setOpenPharmacy(false);
          }
        }}
      />
    </Collapsible>
  );
};
